import { gql } from '@urql/core';
import { Espin_Role_Enum, PlayerProfileFragment } from 'generated/spin-graphql';
import React, { useMemo } from 'react';
import { useAppState } from '../../client/providers/AppProvider';
import { VODsCard } from './PlayerVodsDisplay';
import { PrimaryGameCard } from './PrimaryGameCard';
import { AcademicInterestsCard } from './profile-page/AcademicInterestsCard';
import { ClassOfCard } from './profile-page/ClassOfCard';
import { CommittedToCollegeCard } from './profile-page/CommittedToCollegeCard';
import { GPACard } from './profile-page/GPACard';
import { PlayerBioCard } from './profile-page/PlayerBioCard';
import { ProfileBannerMessage } from './profile-page/ProfileBannerMessage';
import { SchoolsOfInterestCard } from './profile-page/SchoolsOfInterestCard';

gql`
    fragment PlayerProfile on espin_player_profile {
        id
        spin_username
        first_name
        last_name
        bio
        slug
        committed_to_college
        user_id
        phone
        gpa
        graduating_class
        preferred_study_fields
        preferred_schools
        player_vods {
            ...VODsCard
        }
        primary_game: player_games(where: { primary: { _eq: true } }) {
            ...PrimaryGameCard
        }
        user {
            email
            subscription_status {
                is_subscribed
            }
        }

        ...PlayerBioCard
        ...GPACard
        ...ClassOfCard
        ...CommittedToCollegeCard
    }
`;

interface Props {
    player: PlayerProfileFragment;
    examplePlayer?: boolean;
}

export const PlayerProfile: React.FC<Props> = ({ player, examplePlayer = false }) => {
    const hasRightSide = player?.gpa >= 0 || player.graduating_class || player.primary_game || player.player_vods;
    const [primaryGame] = player?.primary_game ?? [];

    // todo - it may be good to have some helper methods to identify the different roles + classes in a simpler manner.

    const { user, subtier } = useAppState();

    const displayBannerMessage = useMemo(() => {
        // is this myself looking at it
        if (user?.id === player.user?.id) {
            return `
            As an unsubscribed player, only Platinum Plan Colleges & Universities can see your contact information. 
            Take your recruiting journey into your own hands and upgrade to be a Platinum Member today. 
            <a class="link" href="/dashboard/subscriptions">Click here to start</a>
            `;
        }
        // if this is my parent looking at it
        if (
            user?.role === Espin_Role_Enum.EspinParentUser &&
            !!user.parent_profile.child_profiles.find((c) => c.user_id === player.user?.id)
        ) {
            return `
            As a parent of an unsubscribed player, only Platinum Plan Colleges & Universities can see your contact information. 
            Take your recruiting journey into your own hands and upgrade to be a Platinum Member today. 

            <a class="link" href="/dashboard/subscriptions">Click here to start</a>
            `;
        }

        // if this is a tier 2 college sub, then yes
        if (user?.role === Espin_Role_Enum.EspinCollegeUser && subtier > 1) {
            return `
            This player's contact information is visible to you as your program is currently a Tier 2 Subscriber. 
            We still highly recommend that you always follow & message any player on the platform first as that player & parent will get the
            notification of your interest in them before contacting them through the listed contact information.
            
            If you need any help, please reach out at <a class="link" href="mailto:info@staypluggedin.com">info@staypluggedin.com</a>`;
        }
        // else no
        return `
        This player's contact information is hidden from view as they are not a platform member. 
        It is highly encouraged that you first follow this player and then message this player. This player will still get a notification that a college is attempting to contact them.
        <br />
        You can view all players' contact information by upgrading to the Platinum Subscription (Tier 2), <a class="link" href="/pricing">click here for more details</a>.
        <br />
        If you would like a direct line of communication with this player, please contact <a class="link" href="mailto:info@staypluggedin.com">info@staypluggedin.com</a>
        `;
    }, [subtier, player, user]);
    return (
        <>
            <div className="left-side-profile">
                {!examplePlayer && !player.user.subscription_status?.is_subscribed && (
                    <ProfileBannerMessage title={'Unsubscribed Player'} copy={displayBannerMessage} />
                )}
                {player.committed_to_college && <CommittedToCollegeCard player={player} />}
                <PlayerBioCard player={player} />
                {player.preferred_study_fields && (
                    <AcademicInterestsCard academicInterests={player.preferred_study_fields} />
                )}
                {player.preferred_schools && <SchoolsOfInterestCard schoolsOfInterest={player.preferred_schools} />}
            </div>
            {hasRightSide && (
                <div className="right-side-profile">
                    {player.gpa >= 0 && <GPACard player={player} />}
                    {player.graduating_class && <ClassOfCard player={player} />}
                    {player.primary_game.length > 0 && <PrimaryGameCard primaryGame={primaryGame} />}
                    {player.player_vods.length > 0 && <VODsCard vods={player.player_vods} />}
                </div>
            )}
        </>
    );
};
